import styles from './Asset.scss';

import cx from 'classnames';
import * as Contentful from 'contentful';
import { stringify } from 'qs';
import React from 'react';
import { isWebpSupported } from 'src/utils/BrowserFeatures';

export type PositionFocusType = 'top' | 'left' | 'bottom' | 'right';

export type Props<E extends HTMLElement, A extends React.AllHTMLAttributes<E> = React.AllHTMLAttributes<E>> = A & {
  autoHeight?: boolean;
  autoWidth?: boolean;
  imageHeight?: number;
  imageWidth?: number;
  fit?: 'pad' | 'crop' | 'fill' | 'scale' | 'thumb';
  focus?: PositionFocusType[] | PositionFocusType | 'face' | 'faces';
  format?: 'jpg' | 'png' | 'webp';
  png8?: boolean;
  asset?: Contentful.Asset;
};

const CONTENTFUL_MAX_HEIGHT = 4000;
const CONTENTFUL_MAX_WIDTH = isNaN(parseInt(styles.maxWidth)) ? 4000 : parseInt(styles.maxWidth);

export abstract class Asset<E extends HTMLElement, P extends Props<E>> extends React.Component<P, { isMounted: boolean }> {

  get el() {
    return this.elRef.current || undefined;
  }

  get autoHeight() {
    return !!this.props.autoHeight;
  }

  get autoWidth() {
    return !!this.props.autoWidth;
  }

  get imageHeight() {
    return this.props.imageHeight;
  }

  get imageWidth() {
    return this.props.imageWidth;
  }

  get height() {
    return Math.min(Asset.scale(this.autoHeight, this.imageHeight, this.el && this.el.clientHeight), CONTENTFUL_MAX_HEIGHT);
  }

  get width() {
    return Math.min(Asset.scale(this.autoWidth, this.imageWidth, this.el && this.el.clientWidth), CONTENTFUL_MAX_WIDTH);
  }

  get asset() {
    return this.props.asset;
  }

  get fields() {
    return this.asset && this.asset.fields;
  }

  get file() {
    return this.fields && this.fields.file;
  }

  get fileUrl() {
    return this.file && this.file.url;
  }

  get alt() {
    return this.fields && this.fields.description;
  }

  get fit() {
    return this.props.fit || 'scale';
  }

  get format() {
    return this.props.format;
  }

  get isMounted() {
    return this.state.isMounted;
  }

  get isSvg() {
    return (this.contentType || '').indexOf('svg') > -1;
  }

  get isPng() {
    return this.contentType === 'image/png' || this.format === 'png';
  }

  get isJpeg() {
    return this.contentType === 'image/jpeg' || this.format === 'jpg';
  }

  get focus() {
    const { focus } = this.props;
    if (!focus) {
      return undefined;
    }
    if (Array.isArray(this.props.focus)) {
      return (focus as string[]).filter(s => s && s.length).join('_');
    }
    return `${focus}`;
  }

  get assetUrl() {
    if (
      !this.fileUrl ||
      ((this.autoHeight || this.autoWidth) && !this.state.isMounted && !this.el)
    ) return;

    return [this.fileUrl, stringify(this.querystring)].filter(s => !!s).join('?');
  }

  get contentType() {
    return this.asset && this.asset.fields.file.contentType;
  }

  get png8() {
    return this.props.png8;
  }

  /**
   * Change how we ask contentful for images
   * https://www.contentful.com/developers/docs/references/images-api/#/reference/changing-formats
   */
  get querystring() {
    if (this.isSvg && !this.format) return {};

    let fl;
    // Prefer webp if the browser supports it over optimized jpegs/png8
    if (!isWebpSupported) {
      fl = cx({
        progressive: !this.isPng && this.isJpeg,
        png8: !this.isJpeg && this.isPng && this.png8,
      });
    }

    return {
      fit: this.fit,
      fm: isWebpSupported ? 'webp' : this.format,
      fl,
      h: !this.isSvg && this.height > 0 && this.width < CONTENTFUL_MAX_WIDTH ? this.height : undefined,
      w: !this.isSvg && this.width > 0 ? this.width : undefined,
      f: this.focus,
    };
  }

  protected elRef = React.createRef<E>();

  protected intervalId: NodeJS.Timer;

  static scale(autoSize: boolean, imageSize?: number, clientSize?: number): number {
    const scale = autoSize ? Math.abs(imageSize || 1) : 1;

    const value = (autoSize && clientSize !== undefined) ? clientSize * scale : (imageSize || 0);

    return Math.ceil(window.devicePixelRatio * (value || 0));
  }

  constructor(props: P, context?: any) {
    super(props, context);
    this.state = {
      isMounted: false,
    };
  }

  componentDidMount() {
    if (this.autoHeight || this.autoWidth) {
      const checker = () => {
        if (this.height === 0 && this.width === 0) {
          requestAnimationFrame(checker);
          return;
        }
        // force re-render
        this.setState({ isMounted: true });
      };
      checker();
    }

  }
}
